// ABOUTME: Chat card for an open variation group: the options as exact platform
// mockups (as made, for options no post plan places yet) in a swipeable carousel,
// "Use option N" (owner only, through the one pick endpoint) and "Tweak" into the
// existing draft editor.
import { useEffect, useMemo, useState } from 'react'
import { Check, Loader2, PencilLine } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OptionMedia } from '@/components/preview/OptionMedia'
import { PlatformMockup } from '@/components/preview/PlatformMockup'
import { isApiError } from '@/api/client'
import { pickVariation } from '@/api/variations'
import { hasBusinessRoleAtLeast } from '@/lib/permissions'
import { useAuthStore, selectUser } from '@/stores/useAuthStore'
import { useDraftsStore } from '@/stores/useDraftsStore'
import { useGroupPreview } from './useGroupPreview'

interface VariationsCardProps {
  groupId: string
  onTweak: (draftId: string) => void
}

export function VariationsCard({ groupId, onTweak }: VariationsCardProps) {
  const user = useAuthStore(selectUser)
  const isOwner = hasBusinessRoleAtLeast(user?.business_role, 'owner')
  const allDrafts = useDraftsStore((s) => s.drafts)
  const fetchDrafts = useDraftsStore((s) => s.fetchDrafts)
  const preview = useGroupPreview(groupId)
  const [picking, setPicking] = useState<number | null>(null)
  const [pickedPosition, setPickedPosition] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Options are the group's drafts oldest first: option N is the Nth draft.
  const drafts = useMemo(
    () =>
      allDrafts
        .filter((d) => d.variation_group_id === groupId)
        .sort((a, b) => a.created_at.localeCompare(b.created_at)),
    [allDrafts, groupId]
  )

  useEffect(() => {
    if (drafts.length === 0) void fetchDrafts({ silent: true })
  }, [drafts.length, fetchDrafts])

  if (preview.isLoading) {
    return (
      <Loader2
        className="text-muted-foreground m-3 size-5 animate-spin"
        aria-label="Loading options"
      />
    )
  }
  const data = preview.data
  if (!data || data.variants.length === 0) return null

  const choose = async (position: number) => {
    const draft = drafts[position - 1]
    if (!draft) return
    setPicking(position)
    setError(null)
    try {
      await pickVariation(groupId, draft.id, crypto.randomUUID())
      setPickedPosition(position)
      void fetchDrafts({ silent: true })
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Could not choose this option')
    } finally {
      setPicking(null)
    }
  }

  return (
    <div className="my-1.5 w-full" data-testid="variations-card">
      <p className="text-muted-foreground mb-1.5 text-xs">
        {data.variants.length} options — swipe to compare
      </p>
      <div
        className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-2"
        data-testid="variations-carousel"
      >
        {data.variants.map((variant) => {
          const surface = variant.surfaces[0]
          const spec = surface ? data.surfaces[surface.surface] : undefined
          const media = variant.media.filter((_, i) => surface?.media_indexes.includes(i))
          const draft = drafts[variant.position - 1]
          return (
            <div
              key={variant.position}
              className="flex w-[min(300px,85%)] shrink-0 snap-center flex-col gap-2"
              data-testid={`variation-option-${variant.position}`}
            >
              {surface && spec ? (
                <PlatformMockup
                  spec={spec}
                  surface={surface}
                  media={media}
                  businessName={data.business.name}
                />
              ) : (
                <OptionMedia media={variant.media} />
              )}
              <div className="flex gap-2">
                {isOwner && (
                  <Button
                    className="min-h-12 flex-1"
                    onClick={() => void choose(variant.position)}
                    disabled={picking !== null || pickedPosition !== null}
                    data-testid={`use-option-${variant.position}`}
                  >
                    {picking === variant.position ? (
                      <Loader2 className="mr-1 size-4 animate-spin" />
                    ) : pickedPosition === variant.position ? (
                      <Check className="mr-1 size-4" />
                    ) : null}
                    {pickedPosition === variant.position
                      ? 'Chosen'
                      : `Use option ${variant.position}`}
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="min-h-12 flex-1"
                  disabled={!draft}
                  onClick={() => draft && onTweak(draft.id)}
                  data-testid={`tweak-option-${variant.position}`}
                >
                  <PencilLine className="mr-1 size-4" />
                  Tweak
                </Button>
              </div>
            </div>
          )
        })}
      </div>
      {error && (
        <p role="alert" className="text-destructive text-sm" data-testid="variations-error">
          {error}
        </p>
      )}
    </div>
  )
}
